/**
 * CCPA/CPRA consent state for non-essential cookies/trackers.
 *
 * Scope: this module governs whether analytics/advertising scripts may load.
 * Strictly-necessary cookies (auth session, sidebar UI state) are not gated.
 *
 * Behavior:
 *   - Global Privacy Control (GPC) signal forces `analytics` to "denied" and
 *     locks the state — required by CPRA § 1798.135(b)(1).
 *   - User choice (banner / Privacy Choices page) is persisted to localStorage.
 *   - State changes dispatch a `dashalign:consent-changed` window event so
 *     listeners (e.g., AnalyticsLoader) can react without prop-drilling.
 */

import { Capacitor } from "@capacitor/core";

export type ConsentValue = "granted" | "denied" | "unset";

export interface ConsentState {
  analytics: ConsentValue;
  updatedAt: number | null;
  version: number;
  gpc: boolean;
}

const STORAGE_KEY = "dashalign.consent.v1";
/** Exported so sign-out (which clears localStorage) can preserve the user's
 *  choice — wiping it made the cookie banner reappear on every future login. */
export const CONSENT_STORAGE_KEY = STORAGE_KEY;
const CONSENT_VERSION = 1;
const EVENT_NAME = "dashalign:consent-changed";

function hasGlobalPrivacyControl(): boolean {
  if (typeof navigator === "undefined") return false;
  // GPC is exposed as a boolean property on navigator.
  // Spec: https://globalprivacycontrol.github.io/gpc-spec/
  return (navigator as unknown as { globalPrivacyControl?: boolean })
    .globalPrivacyControl === true;
}

/**
 * The native (Capacitor) app never loads analytics/tracking scripts. Apple
 * App Review guideline 5.1.2(i) reads any in-app cookie-consent prompt as
 * implied tracking that would require App Tracking Transparency — so on
 * native we force analytics to "denied" (which also suppresses the consent
 * banner, since the banner only shows while analytics is "unset") and never
 * surface a cookie prompt. We do not track users; there is nothing to gate.
 */
function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

function readStored(): Partial<ConsentState> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(state: ConsentState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage may be unavailable (private mode quota, etc.); fall back to
    // session-only behavior — banner will reappear next load.
  }
}

export function getConsent(): ConsentState {
  const gpc = hasGlobalPrivacyControl();
  const stored = readStored();

  // Native app: never track, never prompt (see isNativeApp docs). Locked to
  // "denied" so AnalyticsLoader stays inert and CookieBanner never renders.
  if (isNativeApp()) {
    return {
      analytics: "denied",
      updatedAt: stored?.updatedAt ?? null,
      version: CONSENT_VERSION,
      gpc,
    };
  }

  if (gpc) {
    return {
      analytics: "denied",
      updatedAt: stored?.updatedAt ?? null,
      version: CONSENT_VERSION,
      gpc: true,
    };
  }

  return {
    analytics: stored?.analytics ?? "unset",
    updatedAt: stored?.updatedAt ?? null,
    version: CONSENT_VERSION,
    gpc: false,
  };
}

function setAnalytics(value: ConsentValue): ConsentState {
  const next: ConsentState = {
    analytics: value,
    updatedAt: Date.now(),
    version: CONSENT_VERSION,
    gpc: hasGlobalPrivacyControl(),
  };
  // GPC overrides explicit grants — never persist "granted" while GPC is on.
  const toPersist: ConsentState =
    next.gpc && value === "granted" ? { ...next, analytics: "denied" } : next;
  writeStored(toPersist);
  emitChange(toPersist);
  return toPersist;
}

export function acceptAnalytics(): ConsentState {
  return setAnalytics("granted");
}

export function rejectAnalytics(): ConsentState {
  return setAnalytics("denied");
}

export function resetConsent(): ConsentState {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
  const next = getConsent();
  emitChange(next);
  return next;
}

export function onConsentChange(
  listener: (state: ConsentState) => void,
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<ConsentState>).detail;
    listener(detail);
  };
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}

function emitChange(state: ConsentState): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<ConsentState>(EVENT_NAME, { detail: state }));
}

export const __testing = { STORAGE_KEY, CONSENT_VERSION, EVENT_NAME };
